/**
 * The files on one message, drawn the same way everywhere they are read: the
 * room's chat, the Watch page's chat, the thread under an update, the update
 * itself and the host's inbox. A video is a real player where it sits, with a
 * way to full screen; a picture shows at its own shape and opens large with a
 * way to save it; anything else is a card with its type, its size and a real
 * Download. A voice note keeps its own player. Nothing opens a box over the page
 * just to play.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { Attachment } from "../../shared/types";
import { assetUrl } from "../lib/api";
import { clockSeconds, useNoteStream } from "../lib/noteStream";
import { NotePlayer } from "./NotePlayer";
import "../styles/attachments.css";

/** The speeds the button walks through, in order. */
const RATES = [1, 1.25, 1.5, 2] as const;
const IcSound = (): ReactNode => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5 6 9H2v6h4l5 4V5Z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M18.5 5.5a9 9 0 0 1 0 13" />
  </svg>
);
const IcMuted = (): ReactNode => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5 6 9H2v6h4l5 4V5Z" /><path d="m22 9-6 6M16 9l6 6" />
  </svg>
);

const IcFile = (): ReactNode => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);
const IcDown = (): ReactNode => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v12M6 9l6 6 6-6M4 20h16" />
  </svg>
);
const IcPlay = (): ReactNode => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7 5.5v13l11-6.5z" />
  </svg>
);
const IcPause = (): ReactNode => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
  </svg>
);
const IcFull = (): ReactNode => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
  </svg>
);
const IcCross = (): ReactNode => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/** M:SS for the player's clock. */
function mmss(seconds: number): string {
  const s = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Where a video plays from: a note's gated address, or its own stored object. */
function videoSrc(a: Attachment): string | null {
  if (a.media_url) return a.media_url;
  return a.key ? assetUrl(a.key) : null;
}

/** The type a file card wears: its extension, in capitals, or FILE. */
export function extOf(name: string): string {
  const m = /\.([a-z0-9]{1,5})$/i.exec(name.trim());
  return m ? m[1].toUpperCase() : "FILE";
}

/** The colour a type badge takes; the common office kinds get their own. */
export function badgeTone(ext: string): "pdf" | "doc" | "sheet" | "" {
  const e = ext.toLowerCase();
  if (e === "pdf") return "pdf";
  if (e === "doc" || e === "docx" || e === "pages" || e === "rtf") return "doc";
  if (e === "xls" || e === "xlsx" || e === "csv" || e === "numbers") return "sheet";
  return "";
}

/** A pasted link's embed address, for the hosts that play in a frame. */
export function embedSrc(url: string): string | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  const host = u.hostname.replace(/^www\./, "");
  if (host === "youtu.be") {
    const id = u.pathname.slice(1).split("/")[0];
    return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
  }
  if (host === "youtube.com" || host === "m.youtube.com" || host === "music.youtube.com") {
    const v = u.searchParams.get("v");
    if (v) return `https://www.youtube-nocookie.com/embed/${v}`;
    const path = u.pathname.match(/^\/(shorts|embed|live)\/([\w-]+)/);
    return path ? `https://www.youtube-nocookie.com/embed/${path[2]}` : null;
  }
  if (host === "vimeo.com") {
    const id = u.pathname.match(/^\/(\d+)/);
    return id ? `https://player.vimeo.com/video/${id[1]}` : null;
  }
  if (host === "player.vimeo.com") return url;
  return null;
}

// ── a video, playing where it sits ──────────────────────────────────────
export function AttVideo({
  src,
  poster,
  label,
  duration,
  download = true,
  onStart,
  onFinish,
}: {
  src: string;
  poster?: string;
  label: string;
  /** The length the note carries, in seconds: shown until the video reads its own. */
  duration?: number;
  /** Offer the file itself; off for a video that is a pitch, not a hand-over. */
  download?: boolean;
  /** It started playing (every time), and it played to the end. */
  onStart?: () => void;
  onFinish?: () => void;
}): ReactNode {
  const video = useRef<HTMLVideoElement>(null);
  const stream = useNoteStream(video, src);
  const box = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [total, setTotal] = useState(0);
  const [rate, setRate] = useState<number>(RATES[0]);
  const [muted, setMuted] = useState(false);
  const toggleMute = () => {
    const v = video.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const toggle = () => {
    const v = video.current;
    if (!v) return;
    if (v.paused) void v.play()?.catch(() => {});
    else v.pause();
  };

  const readDuration = () => {
    const v = video.current;
    if (v && Number.isFinite(v.duration) && v.duration > 0) setTotal(v.duration);
  };

  const seek = (clientX: number, el: HTMLElement) => {
    const v = video.current;
    if (!v || total <= 0) return;
    const r = el.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    v.currentTime = frac * total;
    setCur(frac * total);
  };

  const stepRate = () => {
    const next = RATES[(RATES.indexOf(rate as (typeof RATES)[number]) + 1) % RATES.length];
    setRate(next);
    const v = video.current;
    if (v) v.playbackRate = next;
  };

  // Escape leaves full screen on its own; this only asks for it and hands it back.
  const full = () => {
    const el = box.current;
    if (!el) return;
    const leaving = typeof document !== "undefined" && document.fullscreenElement !== null;
    void (leaving ? document.exitFullscreen?.() : el.requestFullscreen?.())?.catch(() => {});
  };

  const pct = total > 0 ? Math.max(0, Math.min(100, (cur / total) * 100)) : 0;
  const length = total > 0 ? total : (duration ?? 0);
  // Once started, the bar stays: a paused video on a phone still needs its scrub.
  const live = playing || cur > 0;

  return (
    <div className={live ? "fw-att-mp fw-live" : "fw-att-mp"} ref={box}>
      <video
        ref={video}
        src={stream ? undefined : src}
        poster={poster}
        playsInline
        preload="metadata"
        aria-label={label}
        onClick={toggle}
        onPlay={() => {
          setPlaying(true);
          onStart?.();
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          onFinish?.();
        }}
        onLoadedMetadata={readDuration}
        onDurationChange={readDuration}
        onTimeUpdate={() => {
          const v = video.current;
          if (v) setCur(v.currentTime);
        }}
      >
        <track kind="captions" />
      </video>
      {!playing && length > 0 ? <span className="fw-att-dur">{mmss(length)}</span> : null}
      {playing ? null : (
        <button type="button" className="fw-att-big" aria-label="Play" onClick={toggle}>
          <IcPlay />
        </button>
      )}
      <div className="fw-att-ctl">
        <button type="button" aria-label={playing ? "Pause" : "Play"} onClick={toggle}>
          {playing ? <IcPause /> : <IcPlay />}
        </button>
        <div
          className="fw-att-seek"
          role="slider"
          aria-label="Position"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
          tabIndex={0}
          onClick={(e) => seek(e.clientX, e.currentTarget)}
          onKeyDown={(e) => {
            const v = video.current;
            if (!v) return;
            if (e.key === "ArrowRight") {
              v.currentTime = Math.min(total, v.currentTime + 5);
              e.preventDefault();
            }
            if (e.key === "ArrowLeft") {
              v.currentTime = Math.max(0, v.currentTime - 5);
              e.preventDefault();
            }
          }}
        >
          <span className="fw-att-track">
            <span className="fw-att-fill" style={{ width: `${pct}%` }} />
            <span className="fw-att-knob" style={{ left: `${pct}%` }} />
          </span>
        </div>
        <span className="fw-att-tm">
          {mmss(cur)} / {mmss(length)}
        </span>
        <button type="button" className="fw-att-rate" aria-label="Speed" onClick={stepRate}>
          {rate}x
        </button>
        <button type="button" aria-label={muted ? "Unmute" : "Mute"} aria-pressed={muted} onClick={toggleMute}>
          {muted ? <IcMuted /> : <IcSound />}
        </button>
        {/* A stream is a playlist of pieces, not one file anyone could keep. */}
        {download && !stream ? (
          <a
            className="fw-att-get"
            href={src}
            download={label}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Download"
            title="Download"
            onClick={(e) => e.stopPropagation()}
          >
            <IcDown />
          </a>
        ) : null}
        <button type="button" aria-label="Full screen" onClick={full}>
          <IcFull />
        </button>
      </div>
    </div>
  );
}

// ── a picture, large over the page, with a way to keep it ───────────────
function Lightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}): ReactNode {
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);

  return createPortal(
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape closes it, above; the close button is the keyboard way.
    <div className="fw-att-lightbox" role="dialog" aria-label={alt} onClick={onClose}>
      <img src={src} alt={alt} />
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: only keeps a click on Save from closing the box. */}
      <div className="fw-att-lbcap" onClick={(e) => e.stopPropagation()}>
        <span>{alt}</span>
        <a href={src} download={alt} target="_blank" rel="noreferrer noopener">
          Save
        </a>
      </div>
      <button type="button" className="fw-att-lbx" aria-label="Close" onClick={onClose}>
        <IcCross />
      </button>
    </div>,
    document.body,
  );
}

// ── the files on one message ────────────────────────────────────────────
export function MessageAttachments({
  attachments,
  compact = false,
  onVideoStart,
  onVideoEnd,
}: {
  attachments: Attachment[];
  /** Chat: the media sizes to the column, the width of a bubble. */
  compact?: boolean;
  /** A video among them started playing, and one played to the end. */
  onVideoStart?: (attachment: Attachment) => void;
  onVideoEnd?: (attachment: Attachment) => void;
}): ReactNode {
  const [large, setLarge] = useState<{ src: string; alt: string } | null>(null);
  const close = useCallback(() => setLarge(null), []);

  if (attachments.length === 0) return null;
  // One picture is itself, at its own shape. TWO OR MORE tile as a gallery
  // instead of stacking huge; any of them opens large.
  const pictures = attachments.filter((a) => a.kind === "image" && a.key);
  const gallery = pictures.length >= 2 ? pictures : [];
  const tiled = new Set(gallery);
  const rest = attachments.filter((a) => !tiled.has(a));
  return (
    <div className={compact ? "fw-atts fw-chat" : "fw-atts"}>
      {gallery.length > 0 ? (
        <div className="fw-att-strip fw-gallery">
          {gallery.map((a, i) => {
            const src = assetUrl(a.key as string);
            return (
              <button
                type="button"
                className="fw-att-tile"
                key={`${src}-${i}`}
                title={a.name}
                onClick={() => setLarge({ src, alt: a.name })}
              >
                <img src={src} alt={a.name} loading="lazy" />
              </button>
            );
          })}
        </div>
      ) : null}
      {rest.map((a, i) => {
        const at = `${a.key ?? a.media_key ?? a.name}-${i}`;
        if (a.kind === "voice") return <NotePlayer attachment={a} key={at} />;
        const href = a.key ? assetUrl(a.key) : null;

        if (a.kind === "video") {
          if (a.url) {
            const embed = embedSrc(a.url);
            if (embed) {
              return (
                <span className="fw-att-embed" key={at}>
                  <iframe
                    src={embed}
                    title={a.name}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </span>
              );
            }
            // A host we can't frame still gets a real way in.
            return (
              <a
                className="fw-att-file"
                key={at}
                href={a.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                <span className="fw-att-ic">
                  <IcFile />
                </span>
                <span className="fw-att-t">
                  <b>{a.name}</b>
                  <span>Watch the video</span>
                </span>
              </a>
            );
          }
          const src = videoSrc(a);
          // A note still being transcoded has nothing to play yet.
          if (!src) return null;
          return (
            <AttVideo
              onStart={() => onVideoStart?.(a)}
              onFinish={() => onVideoEnd?.(a)}
              key={at}
              src={src}
              poster={a.poster_url ?? a.thumb}
              label={a.name}
              duration={clockSeconds(a.duration)}
            />
          );
        }

        if (a.kind === "image" && href) {
          return (
            <button
              type="button"
              className="fw-att-pic"
              key={at}
              onClick={() => setLarge({ src: href, alt: a.name })}
            >
              <img src={href} alt={a.name} loading="lazy" />
            </button>
          );
        }

        const ext = extOf(a.name);
        return (
          <span className="fw-att-file" key={at}>
            <span className="fw-att-ic" data-tone={badgeTone(ext)}>
              {ext}
            </span>
            <span className="fw-att-t">
              <b>{a.name}</b>
              {a.size ? (
                <span>
                  {ext} · {a.size}
                </span>
              ) : null}
            </span>
            {href ? (
              <a className="fw-att-dl" href={href} download={a.name}>
                <IcDown />
                Download
              </a>
            ) : null}
          </span>
        );
      })}
      {large ? <Lightbox src={large.src} alt={large.alt} onClose={close} /> : null}
    </div>
  );
}
